import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@mui/material'
import React from 'react'
import { type FormState, type FieldValues } from 'react-hook-form'
import { type BlockerFunction, useBlocker } from 'react-router-dom'

import { useTranslation } from 'hooks/use-translation'

/**
 * Determines whether navigation should be gated by a confirmation dialog.
 * Accepts either a RHF FormState partial or a custom BlockerFunction to use for comparison.
 */
export const PromptDialog: React.FC<
  | {
      formState: Pick<FormState<FieldValues>, 'isDirty' | 'isSubmitting'>
      compareFn?: never
      reset: () => void
    }
  | {
      formState?: never
      compareFn: BlockerFunction
      reset: () => void
    }
> = ({ formState, compareFn, reset }) => {
  const { trans } = useTranslation()
  const blocker = useBlocker(
    compareFn
      ? compareFn
      : ({ currentLocation, nextLocation }) =>
          currentLocation.pathname !== nextLocation.pathname &&
          (formState.isDirty || formState.isSubmitting)
  )

  return (
    <Dialog
      data-test-prompt-dialog
      aria-labelledby="alert-dialog-title"
      open={blocker.state === 'blocked'}
    >
      <DialogTitle color="error" id="alert-dialog-title" variant="h6">
        {trans('common.prompt_dialog.dialog.title')}
      </DialogTitle>
      <DialogContent>
        <DialogContentText color="text" variant="body1">
          {trans('common.prompt_dialog.dialog.text')}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button data-test-prompt-dialog-cancel onClick={blocker.reset}>
          {trans('cancel')}
        </Button>
        <Button
          autoFocus
          data-test-prompt-dialog-continue
          onClick={() => {
            reset()
            blocker.proceed?.()
          }}
        >
          {trans('continue')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
